'use client';

import React, { useState } from 'react';

import type {
  BookPayload,
  CardLabels,
  EnquirePayload,
  FlatCategoryItem,
} from '../../types';
import PackageRow from './PackageRow';

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

interface CategoryPackagesProps {
  category: FlatCategoryItem;
  ci: number;
  isMobile: boolean;
  basePkgCount: number;
  labels?: CardLabels;
  isLast: boolean;
  onEnquire: (payload: EnquirePayload) => void;
  onBook?: (payload: BookPayload) => void;
}

export default function CategoryPackages({
  category,
  ci,
  isMobile,
  basePkgCount,
  labels,
  isLast,
  onEnquire,
  onBook,
}: CategoryPackagesProps): React.ReactElement {
  const resolvedLabels = category?.labels
    ? { ...labels, ...category.labels }
    : labels;
  const categoryTitle = category?.title || 'Packages';
  const catKey = category?.slug || `cat-${ci}`;
  const pkgs = category?.packages || [];

  const [isExpanded, setIsExpanded] = useState(false);

  const hasMoreThanBase = pkgs.length > basePkgCount;
  const shownPackages = isExpanded ? pkgs : pkgs.slice(0, basePkgCount);
  const shownWithIds = shownPackages.map((pkg, i) => ({
    pkg,
    id: pkg?.id || `${catKey}-pkg-${i}`,
    isFirst: ci === 0 && i === 0,
  }));
  const rows = chunk(shownWithIds, isMobile ? 1 : 3);

  return (
    <div className="mb-12 px-2">
      <h2
        className="scroll-m-44 text-center text-2xl font-bold text-slate-900 md:text-4xl"
        id={categoryTitle}
      >
        {categoryTitle}
      </h2>

      <div className="mt-4 flex flex-col gap-6 md:mt-6">
        {rows.map((row, ri) => (
          <PackageRow
            key={ri}
            row={row}
            categoryTitle={categoryTitle}
            labels={resolvedLabels}
            onEnquire={onEnquire}
            onBook={onBook}
          />
        ))}
      </div>

      {hasMoreThanBase && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setIsExpanded((v) => !v)}
            className="border-info-500-soft text-text-action-teal hover:bg-info-500-soft flex cursor-pointer items-center gap-2 rounded-lg border px-5 py-2 text-base font-bold transition"
          >
            {isExpanded ? 'View Less' : 'View All Packages'}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      )}
      {!isLast && <hr className="border-border-divider-muted mt-12 border-t" />}
    </div>
  );
}
